import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material'
import {
  Email as EmailIcon,
  Send as SendIcon
} from '@mui/icons-material'
import SEO from '@components/SEO'
import { useBetaMode } from '@hooks/useBetaMode'

const ContactPage: React.FC = () => {
  const { beta } = useBetaMode()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const response = await fetch('/api/v1/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error((data as { message?: string }).message || 'Failed to send message.')
      }

      setSuccess(true)
      setFormData({ name: '', email: '', subject: '', message: '' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const contactMethods = [
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Email Support',
      description: 'Email is our primary support channel',
      contact: 'support@cigartrack.app',
      action: 'Send Email'
    }
  ]

  const faqs = [
    {
      question: 'How do I get started with CigarTrack?',
      answer: 'Simply sign up for a free account and follow our onboarding guide to set up your first humidor and add your cigars.'
    },
    {
      question: 'Is there a mobile app available?',
      answer: 'CigarTrack works in mobile browsers on any device. Add it to your home screen via your browser\'s Share menu. Installable app (PWA) support is planned for a future release.'
    },
    {
      question: 'Can I import my existing cigar collection?',
      answer: 'Yes, we support importing from CSV files and other common formats. Contact support for assistance with large imports.'
    },
    {
      question: 'How secure is my data?',
      answer: 'Your data is encrypted and stored securely. We use industry-standard security practices to protect your collection information.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'Paid billing is not live yet. Refund terms will be published before Premium and Pro checkout launches. Contact support with questions in the meantime.'
    }
  ]

  return (
    <Box>
      <SEO
        title="Contact Us - CigarTrack"
        description="Get in touch with the CigarTrack team. We're here to help with questions about your cigar collection, account, or anything else."
        keywords="contact CigarTrack, cigar app support, get in touch"
        url="/contact"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: { xs: 6, md: 8 }
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2
              }}
            >
              Get in Touch
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Have questions about CigarTrack? We&apos;re here to help! 
              Reach out to our team and we&apos;ll get back to you as soon as possible.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Methods */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Contact Us
          </Typography>
          <Grid container spacing={4}>
            {contactMethods.map((method, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 4,
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      {method.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ color: '#8B4513', fontWeight: 600 }}>
                      {method.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {method.description}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                      {method.contact}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        window.location.href = 'mailto:support@cigartrack.app'
                      }}
                      sx={{
                        borderColor: '#8B4513',
                        color: '#8B4513',
                        '&:hover': {
                          borderColor: '#7A3D0F',
                          backgroundColor: 'rgba(139, 69, 19, 0.1)'
                        }
                      }}
                    >
                      {method.action}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Form */}
      <Box sx={{ py: 8, backgroundColor: '#F5F5DC' }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Send us a Message
          </Typography>
          
          <Paper elevation={4} sx={{ p: 4 }}>
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Thank you for your message! We&apos;ll get back to you within 24 hours.
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#8B4513'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#8B4513'
                        }
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#8B4513'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#8B4513'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#8B4513'
                        }
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#8B4513'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#8B4513'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#8B4513'
                        }
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#8B4513'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#8B4513'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#8B4513'
                        }
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#8B4513'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{
                      backgroundColor: '#8B4513',
                      color: 'white',
                      px: 6,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#7A3D0F'
                      },
                      '&:disabled': {
                        backgroundColor: '#ccc'
                      }
                    }}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Frequently Asked Questions
          </Typography>
          <Grid container spacing={4}>
            {faqs.filter((faq) => !beta || !/refund|premium|pro|paid/i.test(`${faq.question} ${faq.answer}`)).map((faq, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ color: '#8B4513', fontWeight: 600 }}
                    >
                      {faq.question}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

    </Box>
  )
}

export default ContactPage

